import { interpretarErro } from "./erro.service";
import { apresentarErro } from "./apresentacao";
import { ehFalhaDoUsuario, ehErroSistemico, jaFoiApresentado } from "./catalogo";

// "cpf-cnpj", "cpfCnpj" e "CpfCnpj" são o mesmo campo: os ref das telas do
// Checkout misturam kebab-case e camelCase, o backend manda camelCase.
const normalizar = (nome) => String(nome).toLowerCase().replace(/[^a-z0-9]/g, "");

// "endereco.cep" -> "cep"; "cartoes[0].numeroDoCartao" -> "numeroDoCartao".
const ultimoSegmento = (campo) =>
  String(campo).replace(/\[\d+\]/g, "").split(".").pop();

/**
 * Mixin global de apresentação de erro.
 *
 * A decisão que ele implementa: falha do usuário aparece como toast (e, quando
 * há campo identificado, destacada no próprio formulário), porque ele resolve
 * ali mesmo. Erro sistêmico abre modal com código de suporte, porque não há o
 * que ele faça sozinho.
 */
export const ErroMixin = {
  methods: {
    /**
     * @param {Error} err erro do Axios (ou qualquer erro de uma cadeia .then)
     * @param {Object} [opcoes]
     * @param {Object} [opcoes.mapaCampos] nome do campo no backend -> ref do input
     * @param {string} [opcoes.prefixoRef] prefixo de ref, igual ao de validarFormulario
     * @param {Array} [opcoes.componentes] componentes filhos (abas, sub-formulários)
     *   cujos $refs também são procurados
     * @param {Function} [opcoes.rotearCampo] (campo) => { vm, campo } | null, para
     *   campo que o front distribui em componentes repetidos (ex.: cartoes[1].x)
     * @param {boolean} [opcoes.emModal] falha do usuário em modal, não toast
     * @param {boolean} [opcoes.silencioso] não apresenta nada, só devolve o interpretado
     * @returns {Object} o erro interpretado, com `camposMarcados`
     */
    tratarErro(err, opcoes = {}) {
      const erro = (err && err.valorem) || interpretarErro(err);

      // Marcar o campo vale sempre; repetir a mensagem, não.
      erro.camposMarcados =
        erro.campos && erro.campos.length
          ? this.marcarErrosNosCampos(erro.campos, opcoes)
          : [];

      if (!opcoes.silencioso && !jaFoiApresentado(err)) this.apresentarErro(erro, opcoes);
      return erro;
    },

    apresentarErro(erro, opcoes = {}) {
      apresentarErro(erro, opcoes);
    },

    /**
     * Destaca os campos culpados usando o showError() que os inputs de
     * src/components/inputs expõem. Devolve o que conseguiu marcar e em qual
     * componente, para a tela poder, por exemplo, abrir a aba certa.
     */
    marcarErrosNosCampos(campos, opcoes = {}) {
      const marcados = [];

      campos.forEach(({ campo, mensagem }) => {
        const rota = typeof opcoes.rotearCampo === "function" && opcoes.rotearCampo(campo);
        if (rota && rota.vm) {
          const alvo = this.localizarInput(rota.vm, rota.campo, opcoes);
          if (alvo) {
            alvo.showError(mensagem);
            marcados.push({ campo, dono: rota.vm });
          }
          return;
        }

        const donos = [this].concat((opcoes.componentes || []).filter(Boolean));
        for (const dono of donos) {
          const alvo = this.localizarInput(dono, campo, opcoes);
          if (alvo) {
            alvo.showError(mensagem);
            marcados.push({ campo, dono });
            return;
          }
        }
      });

      return marcados;
    },

    localizarInput(vm, campo, opcoes = {}) {
      const refs = (vm && vm.$refs) || {};
      const mapa = opcoes.mapaCampos || {};
      const prefixo = opcoes.prefixoRef || "";

      // Candidatos, do mais específico ao mais tolerante: o nome mapeado, o nome
      // inteiro ("endereco.cep") e o último segmento ("cep"). Item de lista
      // ("contatos[0].email") não desce ao último segmento: ele vive numa linha
      // de tabela, e casar com um input de mesmo nome marcaria o campo errado.
      // Para listas que viram componentes, use opcoes.rotearCampo.
      const ehItemDeLista = /\[\d+\]/.test(String(campo));
      const candidatos = (
        ehItemDeLista
          ? [mapa[campo], campo]
          : [mapa[campo], campo, mapa[ultimoSegmento(campo)], ultimoSegmento(campo)]
      )
        .filter(Boolean)
        .map(String);

      const comShowError = (ref) => {
        const alvo = Array.isArray(ref) ? ref[0] : ref;
        return alvo && typeof alvo.showError === "function" ? alvo : null;
      };

      for (const nome of candidatos) {
        const direto = comShowError(refs[nome]) || comShowError(refs[prefixo + nome]);
        if (direto) return direto;
      }

      const chaves = Object.keys(refs).filter((r) => !prefixo || r.startsWith(prefixo));
      for (const nome of candidatos) {
        const alvoNormalizado = normalizar(nome);
        if (!alvoNormalizado) continue;

        // Igual ignorando caixa e separador; depois, ref que termina com o nome
        // ("form-cpf" para "cpf").
        const chave =
          chaves.find((r) => normalizar(r) === alvoNormalizado && comShowError(refs[r])) ||
          chaves.find((r) => normalizar(r).endsWith(alvoNormalizado) && comShowError(refs[r]));
        if (chave) return comShowError(refs[chave]);
      }

      return null;
    },

    ehFalhaDoUsuario,
    ehErroSistemico,
  },
};

export default ErroMixin;
